import { MEASUREMENT_STYLE, measurementState } from 'config/measurement';
import React from 'react';
import user_icon from '../../assets/icons/user.svg';
import { useTranslation } from 'react-i18next';

interface Record {
  id: number;
  name: string;
  phone: string;
  email: string;
  alcoholtests: {
    id: number;
    data: string;
  }[];
  profile_picture?: string;
}

interface SubmissionTableProps {
  records: Record[];
}

const SubmissionTable: React.FC<SubmissionTableProps> = ({ records }) => {
  const { t } = useTranslation('dashboard');

  const getBackgroundColor = (data: string): string => {
    const value = parseFloat(data);
    if (value === 0.0) return 'bg-gray-100 py-1 px-2 rounded-md';
    if (measurementState(value) === 'detected')
      return MEASUREMENT_STYLE.detected.badge;
    if (measurementState(value) === 'exceeded') return MEASUREMENT_STYLE.exceeded.badge;
    return '';
  };

  const getTextColor = (data: string): string => {
    const value = parseFloat(data);
    if (value === 0.0) return MEASUREMENT_STYLE.clear.text; // EV-412。gray-100 の上で 6.48（以前の green-600 は 2.99）
    if (measurementState(value) === 'detected') return MEASUREMENT_STYLE.detected.text;
    if (measurementState(value) === 'exceeded') return MEASUREMENT_STYLE.exceeded.text;
    return 'text-caption';
  };

  const getOrdinalSuffix = (index: number) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const relevantIndex = index + 1; // Index starts from 0, so add 1 for display
    const mod100 = relevantIndex % 100;

    if (mod100 >= 11 && mod100 <= 13) {
      return 'th';
    }

    const mod10 = relevantIndex % 10;
    return suffixes[mod10] || suffixes[0];
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-full rounded-md my-4">
        {/* Table Header */}
        <div className="bg-primary/50 rounded-md text-caption">
          <div className="flex p-2 h-8 items-center">
            <p className="w-9/12">{t('table.driver-name')}</p>
            {Array.from({ length: 8 }, (_, index) => (
              <p key={index} className="w-2/5 text-center">
                {t(`table.${index + 1}${getOrdinalSuffix(index)}-record`)}
              </p>
            ))}
          </div>
        </div>
        {/* Table Body */}
        <div>
          {records.map((record) => (
            <div key={record.id} className="flex h-16 border-b items-center">
              <div className="flex items-center py-1 px-2 w-9/12 space-x-2 truncate">
                <img
                  src={record.profile_picture || user_icon}
                  alt=""
                  className="w-10 h-10 object-cover rounded-full"
                />
                <div>
                  {/* EV-315。表の欄の中の空は「-」（EV-311。以前は空白）。 */}
                  <p className="text-body-2">{record.name || '-'}</p>
                  {/* 2026-10-22 axe（color-contrast）。neutral/75 は白の上で 2.85（基準 4.5）。neutral/100（6.03）にした。 */}
                  <p className="text-caption text-neutral/100">
                    {record.phone || record.email || '-'}
                  </p>
                </div>
              </div>
              {Array.from({ length: 8 }, (_, index) => (
                <div
                  key={index}
                  className="flex w-2/5 justify-center align-center overflow-hidden text-caption"
                >
                  <span
                    className={`inline-block max-w-full truncate ${getBackgroundColor(
                      index < record.alcoholtests.length
                        ? record.alcoholtests[index].data
                        : ''
                    )} ${getTextColor(
                      index < record.alcoholtests.length
                        ? record.alcoholtests[index].data
                        : ''
                    )}`}
                  >
                    {/* EV-315。測定の無い行の最初の回と、値が空の測定は「-」（以前は「データなし」と「 mg/L」）。
                        測定の回数より後ろの回は、まだ測っていない回なので空けたまま。 */}
                    {index < record.alcoholtests.length
                      ? record.alcoholtests[index].data
                        ? `${record.alcoholtests[index].data} mg/L`
                        : '-'
                      : index === 0
                      ? '-'
                      : ''}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubmissionTable;
